'use client';

import { createContext, use, useState, type ReactNode } from 'react';

import { useChartReveal } from '@/components/charts/_shared/useChartReveal';
import { type ChartRevealPhase } from '@/config/state-chart-reveal';
import { GA_EVENTS, GA_PARAMS, type GaChartId } from '@/constants/analytics';
import {
  STATE_CHART_DEFAULT_FLYWAY_FILTER,
  STATE_CHART_DEFAULT_VIEW_MODE,
  type StateChartViewMode,
  type StateFlywayFilterId,
} from '@/constants/stateCharts';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { trackEvent } from '@/lib/analytics/trackEvent';

import { StateChartControls } from './StateChartControls';
import styles from './StateDonutChartChrome.module.css';
import { StateDonutChartLayout } from './StateDonutChartLayout';

import type { Messages } from '@/content/messages';

type ChartChromeState = {
  /** Intent / toggle pressed state. */
  viewMode: StateChartViewMode;
  /** Painted mode — lags `viewMode` until exit completes. */
  displayViewMode: StateChartViewMode;
  flywayFilter: StateFlywayFilterId;
  phase: ChartRevealPhase;
  onExitComplete: () => void;
  onEnterComplete: () => void;
};

const StateDonutChartFiltersContext = createContext<ChartChromeState | null>(
  null,
);

/**
 * Reads view-mode / flyway-filter / reveal phase from the nearest
 * `StateDonutChartChrome`. Must be called from a Client Component
 * descendant (not passed as a render-prop from a Server Component).
 */
export function useStateDonutChartFilters(): ChartChromeState {
  const value = use(StateDonutChartFiltersContext);
  if (!value) {
    throw new Error(
      'useStateDonutChartFilters must be used within StateDonutChartChrome',
    );
  }
  return value;
}

type StateDonutChartChromeProps = {
  heading: string;
  charts: Messages['state']['charts'];
  datazoneUrl: string;
  chartHeader?: ReactNode;
  chartFooter?: ReactNode;
  children: ReactNode;
  chartId: GaChartId;
};

/**
 * Client chrome for State donut charts: owns view-mode, flyway-filter, and
 * chart reveal orchestration. Layout (xl+ overlay vs in-flow filters) follows
 * `displayViewMode` so the outgoing chart keeps its shell until exit finishes.
 *
 * Compact flyway carousel shares this `chromeScope` with the chart. Controls +
 * plate share `mainRow`; callers compose optional chart-header and chart-footer
 * slots according to the chart's information hierarchy.
 */
export function StateDonutChartChrome({
  heading,
  charts,
  datazoneUrl,
  chartHeader,
  chartFooter,
  children,
  chartId,
}: StateDonutChartChromeProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [viewMode, setViewMode] = useState<StateChartViewMode>(
    STATE_CHART_DEFAULT_VIEW_MODE,
  );
  const [flywayFilter, setFlywayFilter] = useState<StateFlywayFilterId>(
    STATE_CHART_DEFAULT_FLYWAY_FILTER,
  );

  const {
    ref,
    displayViewMode,
    phase,
    onExitComplete,
    onEnterComplete,
  } = useChartReveal({ viewMode, reducedMotion });

  const isBarLayout =
    displayViewMode === 'speciesGroup' || displayViewMode === 'habitat';

  function handleViewModeChange(next: StateChartViewMode) {
    setViewMode(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: chartId,
      [GA_PARAMS.flywayId]: flywayFilter,
      [GA_PARAMS.viewMode]: next,
    });
  }

  function handleFlywayFilterChange(next: StateFlywayFilterId) {
    setFlywayFilter(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: chartId,
      [GA_PARAMS.flywayId]: next,
      [GA_PARAMS.viewMode]: viewMode,
    });
  }

  function clearSelection() {
    setViewMode(STATE_CHART_DEFAULT_VIEW_MODE);
    setFlywayFilter(STATE_CHART_DEFAULT_FLYWAY_FILTER);
  }

  return (
    <StateDonutChartFiltersContext
      value={{
        viewMode,
        displayViewMode,
        flywayFilter,
        phase,
        onExitComplete,
        onEnterComplete,
      }}
    >
      <div
        className={styles.chromeScope}
        data-chart-layout={isBarLayout ? 'bars' : 'donut'}
      >
        <div className={styles.mainRow}>
          <StateChartControls
            heading={heading}
            charts={charts}
            datazoneUrl={datazoneUrl}
            viewMode={viewMode}
            layoutViewMode={displayViewMode}
            flywayFilter={flywayFilter}
            onViewModeChange={handleViewModeChange}
            onFlywayFilterChange={handleFlywayFilterChange}
            onClearSelection={clearSelection}
          />

          <StateDonutChartLayout.ChartColumn
            className={
              isBarLayout ? styles.chartColumnBesideFilters : undefined
            }
          >
            {chartHeader}
            <StateDonutChartLayout.ChartPlate>
              <div ref={ref}>{children}</div>
            </StateDonutChartLayout.ChartPlate>
          </StateDonutChartLayout.ChartColumn>
        </div>

        {chartFooter}
      </div>
    </StateDonutChartFiltersContext>
  );
}
